import { useEffect, useMemo, useState } from "react";
import { Building2 } from "lucide-react";
import { listCompanies, type Company, type Status } from "@/components/api/companiesApi";
import {
    ACTIVE_STATUSES,
    STATUS_ORDER,
    filterCompanies,
    sortCompanies,
    type SortKey,
} from "@/lib/cms";
import { Button } from "@/components/ui/button";
import { CompanyCard } from "./components/CompanyCard";
import { CmsControlBar } from "./components/CmsControlBar";
import { NewCompanyModal } from "./components/NewCompanyModal";

export function CmsList() {
    const [companies, setCompanies] = useState<Company[]>([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);

    // Filter / sort / search state
    const [search, setSearch] = useState("");
    const [statuses, setStatuses] = useState<Status[]>(ACTIVE_STATUSES);
    const [overdueOnly, setOverdueOnly] = useState(false);
    const [needsAction, setNeedsAction] = useState(false);
    const [sort, setSort] = useState<SortKey>("nextAction");

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const data = await listCompanies();
                if (active) setCompanies(data);
            } catch {
                // Error already surfaced via errorEvents by the API layer.
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => {
            active = false;
        };
    }, []);

    const visible = useMemo(() => {
        const filters = { statuses, overdueOnly, needsAction, search };
        return sortCompanies(filterCompanies(companies, filters), sort);
    }, [companies, statuses, overdueOnly, needsAction, search, sort]);

    // For the "matches only outside the current filter" hint: how many would
    // show if every status were selected (only meaningful when not searching,
    // since search already bypasses the status filter).
    const hiddenByStatus = useMemo(() => {
        if (search.trim()) return 0;
        return filterCompanies(companies, {
            statuses: STATUS_ORDER,
            overdueOnly,
            needsAction,
            search,
        }).length;
    }, [companies, overdueOnly, needsAction, search]);

    const hasCompanies = companies.length > 0;

    return (
        <div className="p-6 space-y-6 animate-fade-in">
            <div>
                <h1 className="text-2xl font-bold">Customers</h1>
                <p className="text-muted-foreground">
                    Track outreach to real-estate agencies you want as customers.
                </p>
            </div>

            <CmsControlBar
                search={search}
                onSearchChange={setSearch}
                statuses={statuses}
                onStatusesChange={setStatuses}
                overdueOnly={overdueOnly}
                onOverdueOnlyChange={setOverdueOnly}
                needsAction={needsAction}
                onNeedsActionChange={setNeedsAction}
                sort={sort}
                onSortChange={setSort}
                onNewCompany={() => setModalOpen(true)}
            />

            {loading ? (
                <p className="text-muted-foreground py-12 text-center">Loading…</p>
            ) : visible.length > 0 ? (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {visible.map((company) => (
                        <CompanyCard key={company.id} company={company} />
                    ))}
                </div>
            ) : hasCompanies ? (
                <EmptyState
                    title="No companies match"
                    description={
                        hiddenByStatus > 0
                            ? `No matches with the current filters. ${hiddenByStatus} ${
                                  hiddenByStatus === 1 ? "company is" : "companies are"
                              } hidden by the status filter (e.g. Won/Lost) — widen it to see them.`
                            : "No companies match the current filters or search. Try clearing them."
                    }
                />
            ) : (
                <EmptyState
                    title="No companies yet"
                    description="Add your first real-estate agency to start working the pipeline."
                    action={<Button onClick={() => setModalOpen(true)}>New Company</Button>}
                />
            )}

            <NewCompanyModal open={modalOpen} onOpenChange={setModalOpen} />
        </div>
    );
}

function EmptyState({
    title,
    description,
    action,
}: {
    title: string;
    description: string;
    action?: React.ReactNode;
}) {
    return (
        <div className="glass-card flex flex-col items-center text-center gap-3 py-16 px-6">
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <Building2 className="h-6 w-6 text-primary" />
            </div>
            <h3 className="font-semibold">{title}</h3>
            <p className="text-sm text-muted-foreground max-w-md">{description}</p>
            {action}
        </div>
    );
}
